import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, HeartHandshake, Loader2, Quote } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Price } from "@/lib/currency";
import { BESPOKE_COPY, GOOGLE_FORM_URL, IMAGES, waLink } from "@/data/content";
import { Nav } from "@/components/Nav";
import { Seo } from "@/components/Seo";
import { Footer } from "@/components/Footer";
import { FadeUp, Overline, SectionHeading } from "@/components/Reveal";

const JOURNEY_OPTIONS = ["Tourist", "Relocator", "Diaspora Returnee", "Investor", "Expat", "Couple / celebration", "Family", "Group"];

const ConciergeForm = ({ compact = false }) => {
  const [form, setForm] = useState({ name: "", email: "", journey: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", form);
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full border border-jawa-ivory/20 bg-transparent py-3 text-sm text-jawa-ivory outline-none transition-colors placeholder:text-jawa-ivory/40 focus:border-jawa-turquoise";

  if (sent)
    return (
      <p className="text-sm leading-relaxed text-jawa-turquoise" data-testid="concierge-sent">
        Received — the concierge will reply personally, and a confirmation is on its way to your email.
      </p>
    );

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="concierge-form">
      <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" data-testid="concierge-name" className={inputCls} />
      <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email address" data-testid="concierge-email" className={inputCls} />
      <select required value={form.journey} onChange={(e) => setForm({ ...form, journey: e.target.value })} data-testid="concierge-journey" className={`${inputCls} ${form.journey ? "" : "text-jawa-ivory/40"}`}>
        <option value="" disabled className="text-jawa-espresso">I am planning as…</option>
        {JOURNEY_OPTIONS.map((o) => (
          <option key={o} value={o} className="text-jawa-espresso">{o}</option>
        ))}
      </select>
      <textarea required rows={compact ? 3 : 4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="What would you like help with?" data-testid="concierge-message" className={`${inputCls} resize-none`} />
      <button
        type="submit"
        disabled={busy}
        data-testid="concierge-submit-button"
        className="flex items-center gap-2 bg-jawa-coral px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-white transition-colors hover:bg-jawa-turquoise hover:text-jawa-nearblack disabled:opacity-60"
      >
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}
        Send Enquiry
      </button>
    </form>
  );
};

export { ConciergeForm };

const PHASES = [
  { t: "Before you arrive", d: "Research, shortlists, bookings groundwork and the questions you didn't know to ask — resolved while you're still packing." },
  { t: "On arrival", d: "Airport support, SIM, transfers and a first day that runs exactly to plan." },
  { t: "Throughout", d: "One message away for drivers, stays, changes and the everyday practicalities — before, during and after your stay or move." },
];

const USE_CASES = [
  { t: "Tourists", d: "Detty December, beach days and the events worth your flight." },
  { t: "Couples", d: "Honeymoons, proposals, anniversaries and quiet weekends done beautifully." },
  { t: "Families", d: "Child-friendly stays, schools questions and multi-generational plans." },
  { t: "Returnees", d: "Housing, domestic staff, utilities and the first ninety days home." },
  { t: "Expats", d: "Arrival logistics, drivers, banking orientation and daily-life setup." },
  { t: "Investors", d: "Research trips, property viewings and reputable introductions." },
];

export default function ConciergePage() {
  return (
    <div className="bg-jawa-ivory" data-testid="concierge-page">
      <Nav />
      <Seo
        title="The JÀWÀ Concierge — One Message, Everything Arranged"
        description="A personal concierge founded by Elizabeth: arrival support, vetted drivers and stays, curated experiences and honest local guidance — before, during and after your trip or move."
      />
      <header className="relative flex min-h-[75vh] items-end overflow-hidden">
        <img src={IMAGES.concierge} alt="JÀWÀ concierge" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-jawa-nearblack/90 via-jawa-nearblack/45 to-jawa-nearblack/25" />
        <div className="relative mx-auto w-full max-w-7xl px-6 pb-16 pt-40 lg:px-10">
          <FadeUp>
            <p className="text-xs uppercase tracking-[0.35em] text-jawa-turquoise">The JÀWÀ Concierge</p>
            <h1 className="mt-3 max-w-3xl font-serif text-5xl font-medium tracking-tight text-jawa-ivory sm:text-6xl">
              One message. Everything arranged.
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-jawa-ivory/85 md:text-lg">
              Arriving somewhere unfamiliar means juggling a hundred small decisions with no idea whom to
              trust. Our concierge replaces that uncertainty with one accountable, local point of contact.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href={waLink("Hello JÀWÀ Concierge — I need a hand with my plans.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="concierge-whatsapp-hero"
                className="inline-flex items-center gap-2 bg-jawa-turquoise px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-nearblack transition-colors hover:bg-jawa-ivory"
              >
                WhatsApp the Concierge <ArrowUpRight className="h-4 w-4" />
              </a>
              <a
                href={GOOGLE_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="concierge-bespoke-hero"
                className="inline-flex items-center gap-2 border border-jawa-coral px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-coral transition-colors hover:bg-jawa-coral hover:text-white"
              >
                Request a Bespoke Itinerary
              </a>
            </div>
          </FadeUp>
        </div>
      </header>

      <main>
        <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
          <FadeUp>
            <SectionHeading overline="Why it matters" title="Local knowledge changes everything." />
          </FadeUp>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {PHASES.map((p, i) => (
              <FadeUp key={p.t} delay={i * 0.06}>
                <div className="h-full border border-jawa-stone bg-white p-8" data-testid={`concierge-phase-${i}`}>
                  <p className="font-serif text-4xl text-jawa-gold">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-4 font-serif text-2xl font-medium text-jawa-espresso">{p.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-jawa-espresso/70">{p.d}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10" data-testid="concierge-consultations-section">
          <FadeUp>
            <SectionHeading overline="1-2-1 consultations" title="Talk it through with an expert." />
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-jawa-espresso/60">
              A focused private video call — 15, 30, 45 or 60 minutes — on relocation, investment, settling in,
              property or planning your trip. Booked in West Africa Time, with the meeting link in your confirmation.
            </p>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { d: 15, usd: 25 }, { d: 30, usd: 50 }, { d: 45, usd: 75 }, { d: 60, usd: 100 },
              ].map((t) => (
                <Link
                  key={t.d}
                  to="/consultations"
                  data-testid={`concierge-consult-${t.d}`}
                  className="group border border-jawa-espresso/12 bg-white p-6 transition-colors hover:border-jawa-turquoise"
                >
                  <p className="font-serif text-3xl font-medium text-jawa-espresso">{t.d} <span className="text-lg">min</span></p>
                  <p className="mt-1 font-serif text-xl text-jawa-coral"><Price amount={t.usd} from="USD" /></p>
                  <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-espresso/50 group-hover:text-jawa-turquoise">
                    Book a slot <ArrowUpRight className="h-3.5 w-3.5" />
                  </p>
                </Link>
              ))}
            </div>
          </FadeUp>
        </section>

        <section className="py-24 lg:py-32" data-testid="founder-section">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <div className="grid items-start gap-12 lg:grid-cols-[2fr_3fr] lg:gap-20">
              <FadeUp>
                <div data-testid="founder-portrait">
                  <img
                    src={IMAGES.elizabeth}
                    alt="Elizabeth — founder of JÀWÀ Africa"
                    loading="lazy"
                    className="w-full object-cover"
                  />
                  <p className="mt-4 text-xs uppercase tracking-[0.25em] text-jawa-espresso/50">
                    Elizabeth — Founder, JÀWÀ Africa
                  </p>
                </div>
              </FadeUp>
              <div>
                <FadeUp>
                  <Overline>Meet the founder</Overline>
                  <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-jawa-espresso sm:text-4xl">
                    Built by Elizabeth, so coming home feels stress-free.
                  </h2>
                </FadeUp>
                <FadeUp delay={0.08}>
                  <div className="mt-8 space-y-6 text-base leading-relaxed text-jawa-espresso/75" data-testid="founder-story">
                    <p className="font-serif text-2xl leading-snug text-jawa-espresso">
                      “JÀWÀ means ‘come back’ in Yorùbá — and that is what I kept doing.”
                    </p>
                    <p>
                      Elizabeth is a tech babe, fashion designer, property investor and frequent flyer
                      between London and Lagos. Like many Nigerians in the diaspora, she has spent years
                      moving between two worlds — and every trip home brought the same frictions: overpriced
                      rentals, unreliable vendors, confusing land deals, and being overcharged one too many
                      times because of her accent. She grew tired of feeling like a stranger in her own
                      country.
                    </p>
                    <p>
                      So she built the very solution she needed. JÀWÀ Africa is a digital homebase for the
                      diaspora and for anyone drawn to Africa — built on personal experience and local
                      insight, with one belief at its heart: coming back should feel like progress, not
                      punishment. Whether you are visiting, relocating or investing, the aim is the same —
                      better information, honest guidance and people you can actually reach.
                    </p>
                    <p>
                      That is why this concierge is personal. When you message JÀWÀ, you plug into
                      everything Elizabeth wishes she'd had on ground — in her words, “the cousin you wish
                      you had. The one that won't ghost you or give you ‘just manage am’ solutions.”
                    </p>
                  </div>
                </FadeUp>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
          <FadeUp>
            <SectionHeading overline="Who uses the concierge" title="Made for your kind of journey." />
          </FadeUp>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {USE_CASES.map((u, i) => (
              <FadeUp key={u.t} delay={i * 0.04}>
                <div className="h-full border border-jawa-stone bg-white p-7 transition-colors hover:border-jawa-coral/50" data-testid={`concierge-usecase-${u.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <HeartHandshake className="h-5 w-5 text-jawa-turquoise" />
                  <h3 className="mt-3 font-serif text-xl font-medium text-jawa-espresso">{u.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-jawa-espresso/65">{u.d}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </section>

        <section className="border-y border-jawa-stone bg-jawa-stone/40 py-24">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <FadeUp>
              <SectionHeading overline="In their words" title="What good support feels like." />
            </FadeUp>
            <div className="mt-12 grid gap-6 md:grid-cols-2">
              <FadeUp>
                <blockquote className="h-full border border-jawa-stone bg-white p-8" data-testid="concierge-story-osagie">
                  <Quote className="h-5 w-5 text-jawa-gold" />
                  <p className="mt-4 font-serif text-xl leading-snug text-jawa-espresso">
                    “Having someone help me get organised before I landed made a difference.”
                  </p>
                  <footer className="mt-4 text-xs uppercase tracking-[0.2em] text-jawa-terracotta">K. Osagie — Tourist</footer>
                  <a href="/#testimonials" className="mt-3 inline-block text-xs font-semibold uppercase tracking-[0.2em] text-jawa-turquoise hover:text-jawa-espresso">Read the full story</a>
                </blockquote>
              </FadeUp>
              <FadeUp delay={0.08}>
                <blockquote className="h-full border border-jawa-stone bg-white p-8" data-testid="concierge-story-glenn">
                  <Quote className="h-5 w-5 text-jawa-gold" />
                  <p className="mt-4 font-serif text-xl leading-snug text-jawa-espresso">
                    “Getting established takes more than finding a job and somewhere to stay.”
                  </p>
                  <footer className="mt-4 text-xs uppercase tracking-[0.2em] text-jawa-terracotta">Glenn S. — Expat</footer>
                  <a href="/#testimonials" className="mt-3 inline-block text-xs font-semibold uppercase tracking-[0.2em] text-jawa-turquoise hover:text-jawa-espresso">Read the full story</a>
                </blockquote>
              </FadeUp>
            </div>
            <p className="mt-6 text-xs text-jawa-espresso/45">Client stories shared with permission.</p>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
          <div className="grid gap-12 lg:grid-cols-2">
            <FadeUp>
              <SectionHeading overline="Honest scope" title="What we do — and don't." />
              <ul className="mt-8 space-y-4 text-sm leading-relaxed text-jawa-espresso/75">
                <li><strong className="text-jawa-espresso">We do:</strong> coordinate arrival support, vetted drivers and stays; curate experiences and itineraries; introduce reputable professionals; guide school research, household setup and everyday logistics; and remain reachable throughout.</li>
                <li><strong className="text-jawa-espresso">We don't:</strong> provide regulated financial, immigration or legal advice; guarantee third-party outcomes, property purchases or investment returns; or sell guaranteed admission to events. Where a decision carries legal or financial weight, we point you to qualified professionals.</li>
                <li><strong className="text-jawa-espresso">Pricing:</strong> concierge services are separately priced from membership — quoted per request after we understand what you need. JAWA 1-2-1 consultations are bookable directly via the Services page.</li>
              </ul>
            </FadeUp>
            <FadeUp delay={0.08}>
              <div className="bg-jawa-nearblack p-8 lg:p-10" data-testid="concierge-enquiry-panel">
                <h3 className="font-serif text-2xl font-medium text-jawa-ivory">Start the conversation</h3>
                <p className="mt-3 text-sm leading-relaxed text-jawa-ivory/60">
                  Enquiries land directly with the team — expect a personal reply.
                </p>
                <div className="mt-6">
                  <ConciergeForm compact />
                </div>
                <p className="mt-6 text-xs leading-relaxed text-jawa-ivory/40">
                  Prefer a structured itinerary request? {BESPOKE_COPY}{" "}
                  <a href={GOOGLE_FORM_URL} target="_blank" rel="noopener noreferrer" data-testid="concierge-bespoke-link" className="text-jawa-turquoise underline underline-offset-4">
                    Open the itinerary form
                  </a>
                  .
                </p>
              </div>
            </FadeUp>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
